// =============================================
// Backdate Scheduler — rozkłada daty publikacji partii artykułów na X dni wstecz.
//
// Cel: blog wygląda naturalnie (historia 7-90 dni) zamiast wszystkie 40 artów
// z batcha = ten sam dzień. Daty trafiają do:
//   - schema.org datePublished (przez resolveDates fallback)
//   - WordPress post.date_gmt (przez publishQueueService wpPost payload)
//
// Algorytm:
//   - distribution=uniform: równe interwały w oknie
//   - distribution=recent_heavy: skewed do końca okna (więcej w ostatnich dniach)
//   - godziny: weighted bias 9-17 (peak), 6-22 (cały dzień)
//   - sort deterministyczny po hashNodeId (regeneracja → ten sam wynik)
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { hashNodeId } from "@/lib/utils";

export type DistributionMode = "uniform" | "recent_heavy";

export interface BackdateConfig {
  daysBack: number;                  // 1-365
  distribution: DistributionMode;
  workHoursOnly?: boolean;           // true = 9-17, false = 6-22
  endOffsetHours?: number;           // ile godzin przed NOW kończy okno (default 1h)
}

export interface NodeBackdate {
  nodeId: string;
  publishDate: Date;
}

const DEFAULT_END_OFFSET_HOURS = 1;

/**
 * Distribute N daty deterministycznie w oknie [now - daysBack, now - endOffset].
 * Pure function — żadnych DB calls. Pozwala na preview w UI.
 */
export function distributeBackdatedDates(
  nodeIds: string[],
  config: BackdateConfig,
  nowOverride?: Date,
): NodeBackdate[] {
  if (nodeIds.length === 0) return [];
  if (config.daysBack < 1) throw new Error("daysBack must be >= 1");

  const now = nowOverride ?? new Date();
  const endOffsetMs = (config.endOffsetHours ?? DEFAULT_END_OFFSET_HOURS) * 3600_000;
  const endTime = now.getTime() - endOffsetMs;
  const startTime = endTime - config.daysBack * 24 * 3600_000;
  const totalSpan = endTime - startTime;

  // Sort deterministyczny po hashNodeId — regenerate dla tej samej listy = ten sam wynik
  const sorted = [...nodeIds].sort((a, b) => hashNodeId(a) - hashNodeId(b));
  const N = sorted.length;

  const results: NodeBackdate[] = [];
  for (let i = 0; i < N; i++) {
    const nodeId = sorted[i];

    // Position 0..1 w oknie. Dla N=1 → 0.5 (środek).
    const linearPos = N === 1 ? 0.5 : i / (N - 1);

    // Distribution shaping
    let normalizedPos: number;
    if (config.distribution === "recent_heavy") {
      // pow < 1 → skewed do prawej strony (recent days)
      normalizedPos = Math.pow(linearPos, 0.55);
    } else {
      normalizedPos = linearPos;
    }

    // Bazowa pozycja w oknie czasowym
    const baseMs = startTime + normalizedPos * totalSpan;

    // Dodaj noise per-node deterministyczny (mały jitter w obrębie ±12h)
    const hash = hashNodeId(nodeId);
    const jitterRange = Math.min(12 * 3600_000, totalSpan / Math.max(N, 1));
    const jitterMs = ((hash % 1000) / 1000 - 0.5) * jitterRange;

    const dateMs = Math.max(startTime, Math.min(endTime, baseMs + jitterMs));

    // Wymuszenie godzin work-hours bias (9-17) lub szerokiego (6-22)
    const date = new Date(dateMs);
    const hour = config.workHoursOnly
      ? 9 + (hashNodeId(nodeId + "h") % 9)   // 9..17
      : 6 + (hashNodeId(nodeId + "h") % 17); // 6..22
    const minute = hashNodeId(nodeId + "m") % 60;
    date.setHours(hour, minute, 0, 0);

    // Clamp do okna jeśli ustawienie godziny wypchnęło datę poza zakres
    if (date.getTime() > endTime) date.setTime(endTime);
    if (date.getTime() < startTime) date.setTime(startTime);

    results.push({ nodeId, publishDate: date });
  }

  // Zwróć w oryginalnej kolejności wejścia (UI lubi stable)
  const orderMap = new Map(results.map((r) => [r.nodeId, r]));
  return nodeIds.map((id) => orderMap.get(id)!).filter(Boolean);
}

/**
 * Persist rozłożone daty do DB. Nadpisuje istniejące effective_publish_date.
 * Skip dla nodów które są już opublikowane (published_at NOT NULL).
 */
export async function applyBackdatedDates(
  nodeIds: string[],
  config: BackdateConfig,
): Promise<{ updated: number; skippedPublished: number; total: number }> {
  if (nodeIds.length === 0) {
    return { updated: 0, skippedPublished: 0, total: 0 };
  }

  // Wyfiltruj już-opublikowane (nie nadpisujemy historii live)
  const { data: existing } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, published_at")
    .in("node_id", nodeIds);

  const publishedSet = new Set(
    ((existing || []) as Array<{ node_id: string; published_at: string | null }>)
      .filter((r) => r.published_at)
      .map((r) => r.node_id),
  );

  const eligible = nodeIds.filter((id) => !publishedSet.has(id));
  const distributed = distributeBackdatedDates(eligible, config);

  let updated = 0;
  for (const { nodeId, publishDate } of distributed) {
    const { error } = await (supabase as any)
      .from("thematic_node_content")
      .update({ effective_publish_date: publishDate.toISOString() })
      .eq("node_id", nodeId);
    if (!error) updated++;
  }

  return {
    updated,
    skippedPublished: nodeIds.length - eligible.length,
    total: nodeIds.length,
  };
}

/**
 * Clear effective_publish_date (powrót do default flow — NOW przy publish).
 */
export async function clearBackdatedDates(nodeIds: string[]): Promise<number> {
  if (nodeIds.length === 0) return 0;
  const { error } = await (supabase as any)
    .from("thematic_node_content")
    .update({ effective_publish_date: null })
    .in("node_id", nodeIds);
  if (error) return 0;
  return nodeIds.length;
}

/**
 * Fetch effective_publish_date dla listy node_ids (UI preview).
 */
export async function fetchEffectivePublishDates(
  nodeIds: string[],
): Promise<Map<string, Date | null>> {
  if (nodeIds.length === 0) return new Map();
  const { data } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, effective_publish_date")
    .in("node_id", nodeIds);

  const result = new Map<string, Date | null>();
  for (const row of (data || []) as Array<{ node_id: string; effective_publish_date: string | null }>) {
    result.set(row.node_id, row.effective_publish_date ? new Date(row.effective_publish_date) : null);
  }
  return result;
}
